<template>
  <div v-bind:class="isInNewMode ? 'equipment-details':''">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column column is-full">
            <div class="box">
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
                <div v-if="!isEditMode" class="column is-narrow">
                  <b-button class="button" @click="backClicked" icon-left="arrow-left-bold" tabindex="1">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('EquipmentDetails', this.UserActions.UPDATE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" @click="editClicked" icon-left="pencil" tabindex="2">
                    {{ $t('common.edit') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('EquipmentDetails', this.UserActions.DELETE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.DELETE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" type="is-danger" @click="deleteClicked" icon-left="delete"
                            tabindex="3">
                    {{ $t('common.delete') }}
                  </b-button>
                </div>
              </div>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-half">
                  <b-field v-if="isEditMode" :label="$t('equipment-details-view.equipment-model-field')">
                    <BInputWithValidation rules="required" v-model="equipmentData.model"
                                          :placeholder="$t('equipment-details-view.equipment-model-placeholder')"
                                          tabindex="11"></BInputWithValidation>
                  </b-field>
                  <b-field :label="$t('equipment-details-view.equipment-serialnumber-field')">
                    <b-input v-if="isEditMode" v-model="equipmentData.serial_number"
                             :placeholder="$t('equipment-details-view.equipment-serialnumber-placeholder')"
                             tabindex="12"></b-input>
                    <label v-else>{{ equipmentData.serial_number }}</label>
                  </b-field>
                  <b-field :label="$t('equipment-details-view.equipment-category-field')">
                    <b-taginput
                        ref="categoryInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedCategories"
                        :data="filteredCategories"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="true"
                        autocomplete
                        :remove-on-keys=[]
                        :placeholder="$t('equipment-details-view.equipment-category-placeholder')"
                        tabindex="13"
                        @typing="getFilteredCategories"
                        @input="onCategoriesInput"
                        @blur="onCategoryInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedCategories[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('equipment-details-view.equipment-provider-field')">
                    <b-taginput
                        ref="providerInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedProviders"
                        :data="filteredProviders"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="true"
                        autocomplete
                        :remove-on-keys=[]
                        :placeholder="$t('equipment-details-view.equipment-provider-placeholder')"
                        tabindex="14"
                        @typing="getFilteredProviders"
                        @input="onProvidersInput"
                        @blur="onProviderInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedProviders[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('equipment-details-view.equipment-text-field')">
                    <b-input v-if="isEditMode" v-model="equipmentData.text"
                             :placeholder="$t('equipment-details-view.equipment-text-placeholder')" tabindex="14"
                             type="textarea"></b-input>
                    <label v-else style="white-space: pre-wrap;">{{ equipmentData.text }}</label>
                  </b-field>
                </div>
                <div class="column">
                  <b-field :label="$t('equipment-details-view.equipment-quantity-field')">
                    <div class="rows">
                      <div v-if="isEditMode" class="columns">
                        <b-input v-model="equipmentData.meta.estimation.quantity"
                                 class="column" type="number" min=0 step="any"
                                 :placeholder="$t('equipment-details-view.equipment-quantity-placeholder')"
                                 tabindex="16"></b-input>
                        <label class="column">{{ $t("enums.duration-unit.day") }}</label>
                      </div>
                      <label v-else>{{
                          getFormattedQuantityEquipment(equipmentData.meta.estimation, equipmentData.unit)
                        }}
                      </label>
                    </div>
                  </b-field>
                  <b-field :label="$t('equipment-details-view.equipment-fee-per-unit-field')">
                    <div v-if="isEditMode" class="columns">
                      <b-input class="column estimation-value" v-model="equipmentData.meta.estimation.fee_per_unit"
                               :placeholder="$t('equipment-details-view.equipment-fee-per-unit-placeholder')"
                               type="number" min=0 step="any" tabindex="18"></b-input>
                      <label class="column">{{ this.defaultValues.priceUnit }}</label>
                    </div>
                    <label v-else>{{ getFormattedFeePerUnit(equipmentData.meta.estimation) }}</label>
                  </b-field>
                  <label v-if="isEditMode && invalidEstimation"
                         class="required-field">{{ $t('validation.equipment-details') }}
                  </label>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px" v-if="isEditMode">
                <div class="column is-narrow">
                  <b-button class="button" @click="cancelClicked" icon-left="close-thick" tabindex="31">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button v-if="((!this.is_template && currentUserAccess('EquipmentDetails', this.UserActions.UPDATE, this.project_id))
                                                 || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                            class="button is-primary" @click="saveClicked" icon-left="content-save" tabindex="32">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";
import BInputWithValidation from "./inputs/BInputWithValidation";

export default {
  name: "EquipmentDetails",
  components: {
    BInputWithValidation
  },
  props: {
    'project_id': {
      type: String,
    },
    'equipment_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      equipmentData: {
        meta: {},
        unit: ""
      },
      isLoading: false,
      isEditMode: false,
      invalidEstimation: false,
      isFetchingCategories: false,
      categoricalData: {},
      filteredCategories: [],
      selectedCategories: [],
      filteredProviders: [],
      selectedProviders: []
    }
  },
  methods: {
    readEquipmentData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const getEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getEquipmentFunction({
        action: "get_equipment_by_id",
        project_id: this.project_id,
        document_id: this.equipment_id,
        fill_meta: true
      }).then((result) => {
        this.isLoading = false;
        console.log("read equipment result: ", result.data);
        this.equipmentData = result.data["equipments"];
        if (!this.equipmentData.meta.estimation)
          this.equipmentData.meta.estimation = {};
        const category = this.equipmentData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
        const provider = this.equipmentData.provider
        if (provider !== undefined && provider !== "") {
          this.selectedProviders[0] = provider
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting equipments: ", error);
      });
    },
    createEquipment: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const insertEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_equipment";
      insertEquipmentFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("create equipment result: ", result.data);
        this.showSuccess(this.$t('messages.equipment-created'));
        this.$emit('equipmentAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(error.message)
      });
    },
    updateEquipment: function (updateData) {
      this.isLoading = true;
      console.log(this.equipmentData);
      const functions = firebase.app().functions("europe-west3");
      const updateEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_equipment";
      updateEquipmentFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("result: ", result.data);
        this.showSuccess(this.$t('messages.equipment-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting equipment: ", error);
      });
    },
    deleteEquipment: function () {
      this.isLoading = true;
      console.log(this.equipmentData);
      const functions = firebase.app().functions("europe-west3");
      const deleteEquipmentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteEquipmentFunction({
        action: "delete_equipment",
        project_id: this.project_id,
        document_id: this.equipment_id,
      }).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("delete equipment result: ", result.data);
        this.showSuccess(this.$t('messages.equipment-deleted'));
        this.$emit('equipmentDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting equipment: ", error);
      });
    },
    readCategoricalData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions("europe-west3");
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["equipments"]
      }).then((result) => {
        this.isFetchingCategories = false
        console.log("read categories result: ", result.data);
        this.categoricalData = result.data["equipments"]
        this.filteredCategories = this.categoricalData.category
        this.filteredProviders = this.categoricalData.provider
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories: ", error);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    deleteClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('equipment-details-view.delete-equipment-confirm-title').toString(),
        message: this.$t('equipment-details-view.delete-equipment-confirm-message').toString(),
        confirmText: this.$t('equipment-details-view.delete-equipment-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        onConfirm: () => {
          this.deleteEquipment();
        }
      });
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readEquipmentData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back()
    },
    saveClicked: function () {
      const dataFieldsToUpdate = {
        project_id: this.project_id,
        model: this.equipmentData.model,
        serial_number: this.equipmentData.serial_number,
        text: this.equipmentData.text,
        estimation: {}
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (this.selectedProviders[0] !== undefined) {
        dataFieldsToUpdate.provider = this.selectedProviders[0]
      }
      let estimation = this.equipmentData.meta.estimation;
      if (estimation.quantity == null || estimation.quantity === "" || estimation.quantity === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.quantity = parseFloat(this.equipmentData.meta.estimation.quantity);
        this.equipmentData.unit = this.defaultValues.equipmentQuantityUnitId;
        dataFieldsToUpdate.unit = this.equipmentData.unit;
      }
      if (estimation.fee_per_unit == null || estimation.fee_per_unit === "" || estimation.fee_per_unit === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.fee_per_unit = parseFloat(this.equipmentData.meta.estimation.fee_per_unit);
      }

      if (this.isInNewMode) {
        this.createEquipment(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.document_id = this.equipment_id;
        this.updateEquipment(dataFieldsToUpdate);
      }
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoricalData.category
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoricalData.category.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onProvidersInput() {
      this.filteredProviders = this.categoricalData.provider
    },
    getFilteredProviders(text) {
      this.filteredProviders = this.categoricalData.provider.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.addTag()
    },
    onProviderInputBlur() {
      this.$refs.providerInput.addTag()
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("EquipmentDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readCategoricalData()
    if (this.isInNewMode) {
      console.log("creating new equipment");
      this.equipmentData.meta = {};
      this.equipmentData.meta.estimation = {};
      this.isEditMode = true;
    } else {
      console.log("edit equipment : ", this.equipment_id);
      this.isEditMode = false;
      this.readEquipmentData();
    }
  },
  computed: {
    isInNewMode() {
      return this.equipment_id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('equipment-details-view.new-equipment-title')
      } else if (this.isEditMode) {
        return this.$t('equipment-details-view.edit-equipment-title') + " " + this.getAdjustedTitle(this.equipmentData.model)
      } else {
        return this.$t('equipment-details-view.view-equipment-title') + " " + this.getAdjustedTitle(this.equipmentData.model)
      }
    }

  }
};
</script>

<style scoped>
.estimation-value {
  max-width: 50%;
}

.required-field {
  color: red;
  font-size: 10pt;
}
</style>
